import { Alert } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { IReduxState } from '../../../../../configureStore';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import type { IProcedureInvitation } from './GgzbTherV1RequestRead';
import { ThirdPartyMessageStatus } from '../../../../../../common/enums/thirdPartyMessageStatus';
import { translateKey } from '../../../../../translate';
import { RequestCounselingReadButtons } from '../../../../elements/carePathButtons/RequestCounselingReadButtons';
import { FormParagraph, InfoTooltip } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InvitationStatus } from '../../../../elements/InvitationStatus';
import { ProfileImages } from '../../utils/v1/ProfileImages';

export interface ICustomControlRequestReadSentProps extends ICustomControlRequestReadProps {
    invitations: IProcedureInvitation[] | null;
}

export function GgzbTherV1RequestReadSent(props: ICustomControlRequestReadSentProps) {
    const failedToSendAllInvitations = !!props.invitations?.every(
        (invitation) => invitation.thirdPartyMessageStatus === ThirdPartyMessageStatus.failed
    );
    const failedToGetAllProgress = !!props.invitations?.every((invitation) => invitation.failedToGetProgress);
    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const showToolTip = () => {
        return (
            !props.request.authorization?.hasAccessToCounseling &&
            props.invitations?.some((invitation) => invitation.request.requesterPractitionerId !== practitionerId)
        );
    };

    return (
        <>
            <FormSection>
                <ProfileImages
                    alternativeLabelText={'processDetails.counselor'}
                    request={props.request}
                    showAlsoWhenNotRequested
                />
            </FormSection>

            {failedToSendAllInvitations && (
                <Alert appearance="warning">{translateKey('validation.failedToSendAllInvitations')}</Alert>
            )}
            {!failedToSendAllInvitations && failedToGetAllProgress && (
                <Alert appearance="warning">{translateKey('validation.failedToGetUpdatedInvitationProgress')}</Alert>
            )}
            <HorizontalFormGroupRead>
                <FormLabel>
                    Programma's
                    {props.invitations && showToolTip() && (
                        <InfoTooltip text={translateKey('therapieland.tooltipTextBegeleiding')} />
                    )}
                </FormLabel>
                <InputContainer>
                    {props.invitations &&
                        props.invitations.map((invitation, key) => (
                            <InvitationStatus
                                allInvitationsFailedToSend={failedToSendAllInvitations}
                                failedToGetAllProgress={failedToGetAllProgress}
                                failedToGetProgress={invitation.failedToGetProgress}
                                hasAccessToCounseling={props.request.authorization?.hasAccessToCounseling || false}
                                key={key}
                                linkId={uuidv4()}
                                messageStatus={invitation.thirdPartyMessageStatus}
                                percentage={invitation.customControlData.percentage || 0}
                                requesterOrganizationId={invitation.request.requesterOrganizationId}
                                requesterPractitionerId={invitation.request.requesterPractitionerId}
                                requestId={invitation.request.id}
                                requestStatus={invitation.request.status}
                                therapielandCounselingId={invitation.customControlData.id}
                                title={invitation.customControlData.treatmentName}
                            ></InvitationStatus>
                        ))}
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>E-mailadres</FormLabel>
                <InputContainer>
                    <FormParagraph>{props.request.customControlData.emailadres}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <RequestCounselingReadButtons {...props} />
        </>
    );
}
